<script>
import { useOrthancStore } from '@src/store/dicom-web/orthanc-store';
import { ref, watch } from 'vue';

function stripMainDicomTags(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripMainDicomTags);
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([k]) => k !== 'MainDicomTags')
      .map(([k, v]) => [k, stripMainDicomTags(v)]),
  );
}

function formatObj(obj, indent = 0) {
  if (obj === null) return 'null';
  if (typeof obj !== 'object') return String(obj);
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    const pad = '  '.repeat(indent);
    return obj.map(v => `${pad}- ${formatObj(v, indent + 1)}`).join('\n');
  }
  const pad = '  '.repeat(indent);
  const lines = Object.entries(obj).map(([k, v]) => {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      return `${pad}${k}:\n${formatObj(v, indent + 1)}`;
    }
    return `${pad}${k}: ${formatObj(v, indent)}`;
  });
  return lines.join('\n');
}

export default {
  setup() {
    const orthancStore = useOrthancStore();
    const isOpen = ref(false);
    const orthancInfoText = ref('');
    const rebuildMetadataLoading = ref(false);
    const rebuildMetadataMessage = ref('');

    async function loadOrthancInfo() {
      try {
        await orthancStore.connect();
        const [systemInfo, statistics] = await Promise.all([
          orthancStore.fetchSystemInfo().catch(() => null),
          orthancStore.fetchStatistics().catch(() => null),
        ]);
        const clean = o => (o ? stripMainDicomTags(o) : null);
        const parts = [];
        parts.push(
          `/system\n${systemInfo ? formatObj(clean(systemInfo)) : '—'}`,
        );
        parts.push(
          `\n/statistics\n${statistics ? formatObj(clean(statistics)) : '—'}`,
        );
        orthancInfoText.value = parts.join('\n');
      }
      catch (e) {
        orthancInfoText.value = `Error: ${
          e instanceof Error ? e.message : String(e)
        }`;
      }
    }

    async function rebuildMetadata() {
      rebuildMetadataLoading.value = true;
      rebuildMetadataMessage.value = 'Connecting…';
      try {
        await orthancStore.connect();
        const stats = await orthancStore.fetchStatistics();
        const count = stats?.CountStudies ?? orthancStore.studies?.length ?? 0;
        rebuildMetadataMessage.value = `Rebuilding ${count} studies…`;
        const result = await orthancStore.rebuildAllOcg19Metadata();
        console.log('rebuildAllOcg19Metadata', result);
        if (result && typeof result === 'object') {
          const { total, success, failed } = result;
          rebuildMetadataMessage.value
            = typeof total === 'number'
              ? `Done: ${success ?? 0} succeeded, ${
                failed ?? 0
              } failed (${total} studies)`
              : 'Done.';
        }
        else {
          rebuildMetadataMessage.value = result ? 'Done.' : 'No response.';
        }
        await loadOrthancInfo(); // refresh /statistics
      }
      catch (e) {
        rebuildMetadataMessage.value = `Error: ${
          e instanceof Error ? e.message : String(e)
        }`;
      }
      finally {
        rebuildMetadataLoading.value = false;
      }
    }

    const open = () => {
      isOpen.value = true;
      loadOrthancInfo();
    };

    const close = () => {
      isOpen.value = false;
    };

    watch(isOpen, (isNowOpen) => {
      if (isNowOpen) loadOrthancInfo();
    });

    return {
      isOpen,
      orthancStore,
      orthancInfoText,
      rebuildMetadataLoading,
      rebuildMetadataMessage,
      rebuildMetadata,
      open,
      close,
    };
  },
};
</script>

<template>
  <v-dialog v-model="isOpen" max-width="700px">
    <v-card class="system-dialog-card">
      <v-card-title class="text-headline-small">
        Orthanc Server
      </v-card-title>
      <v-card-text>
        <v-textarea
          :model-value="orthancInfoText"
          label="/system and /statistics"
          variant="outlined"
          readonly
          auto-grow
          rows="16"
          class="orthanc-info-text"
        />
        <div class="d-flex align-center mt-3">
          <v-btn
            color="primary"
            variant="elevated"
            :loading="rebuildMetadataLoading"
            :disabled="!orthancStore.isConnected"
            @click="rebuildMetadata"
          >
            Rebuild OCG19 metadata (all studies)
          </v-btn>
          <span v-if="rebuildMetadataMessage" class="ml-3 text-body-small">{{
            rebuildMetadataMessage
          }}</span>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" variant="text" @click="close">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.system-dialog-card {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.system-dialog-card :deep(.v-card-text) {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.orthanc-info-text {
  font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, monospace;
  font-size: 0.6rem;
  line-height: 1.35;
  letter-spacing: 0.02em;
}
</style>
